import { useEffect, useMemo, useState } from 'react';
import { api, type ResidencyRow } from '@/api/client';
import { mockResidency, useMock } from '@/api/mock';

interface TransferEvent {
  id: string;
  from: string;
  to: string;
  dataType: string;
  legalBasis: string;
  status: 'Allowed' | 'Review' | 'Blocked';
  happenedAt: string;
}

const mockEvents: TransferEvent[] = [
  { id: 'evt-001', from: 'DE', to: 'US', dataType: 'Telemetry', legalBasis: 'SCC', status: 'Allowed', happenedAt: '2026-06-08 09:30' },
  { id: 'evt-002', from: 'FR', to: 'IN', dataType: 'PII', legalBasis: 'Pending DPA', status: 'Review', happenedAt: '2026-06-08 14:10' },
  { id: 'evt-003', from: 'JP', to: 'RU', dataType: 'Logs', legalBasis: 'No legal basis', status: 'Blocked', happenedAt: '2026-06-09 01:12' },
  { id: 'evt-004', from: 'GB', to: 'US', dataType: 'Model Features', legalBasis: 'UK IDTA', status: 'Allowed', happenedAt: '2026-06-09 08:21' },
];

export function DataResidencyPage() {
  const [rows, setRows] = useState<ResidencyRow[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<ResidencyRow | null>(null);

  useEffect(() => {
    if (useMock()) {
      setRows(mockResidency);
      return;
    }
    api.residencyMap().then(setRows).catch(() => setRows(mockResidency));
  }, []);

  const summary = useMemo(() => {
    const compliant = rows.filter(r => r.isCompliant).length;
    const nonCompliant = rows.filter(r => !r.isCompliant).length;
    const totalAssets = rows.reduce((sum, r) => sum + r.assetCount, 0);
    return { compliant, nonCompliant, totalAssets };
  }, [rows]);

  const countryAssets = useMemo(() => {
    if (!selectedCountry) return [] as { name: string; type: string; region: string }[];
    const base = selectedCountry.country;
    return [
      { name: `${base.toLowerCase()}-ai-storage`, type: 'Storage Account', region: selectedCountry.countryName },
      { name: `${base.toLowerCase()}-model-endpoint`, type: 'AKS Endpoint', region: selectedCountry.countryName },
      { name: `${base.toLowerCase()}-audit-cosmos`, type: 'Cosmos DB', region: selectedCountry.countryName },
    ];
  }, [selectedCountry]);

  const complianceChecks = useMemo(() => {
    if (!selectedCountry) return [] as { item: string; result: 'Pass' | 'Review' | 'Fail' }[];
    if (selectedCountry.isCompliant) {
      return [
        { item: 'Data localization policy', result: 'Pass' },
        { item: 'Cross-border transfer legal basis', result: 'Pass' },
        { item: 'Retention and deletion controls', result: 'Pass' },
      ];
    }
    return [
      { item: 'Data localization policy', result: 'Review' },
      { item: 'Cross-border transfer legal basis', result: 'Fail' },
      { item: 'Retention and deletion controls', result: 'Review' },
    ];
  }, [selectedCountry]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Data Residency</h1>
        <p className="text-sm text-ink-muted mt-1">Cross-border data flow, regional compliance status, and transfer event logs.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card"><div className="card-title">Compliant Countries</div><div className="kpi-value text-good">{summary.compliant}</div></div>
        <div className="card"><div className="card-title">Non-Compliant Countries</div><div className="kpi-value text-bad">{summary.nonCompliant}</div></div>
        <div className="card"><div className="card-title">Total Assets Tracked</div><div className="kpi-value">{summary.totalAssets}</div></div>
      </div>

      <div className="card overflow-auto">
        <div className="card-title mb-3">Country Compliance Matrix</div>
        <table className="w-full text-sm min-w-[700px]">
          <thead className="text-ink-muted border-b border-border">
            <tr>
              <th className="text-left py-2">Country</th>
              <th className="text-left py-2">Code</th>
              <th className="text-left py-2">Assets</th>
              <th className="text-left py-2">Status</th>
              <th className="text-left py-2">Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.country} className="border-b border-border/50 last:border-0 hover:bg-bg-soft/50 cursor-pointer" onClick={() => setSelectedCountry(r)}>
                <td className="py-2 text-accent-cyan">{r.countryName}</td>
                <td className="py-2">{r.country}</td>
                <td className="py-2">{r.assetCount}</td>
                <td className="py-2">
                  <span className={r.isCompliant ? 'badge-good' : 'badge-bad'}>{r.isCompliant ? 'Compliant' : 'Non-Compliant'}</span>
                </td>
                <td className="py-2 text-ink-muted">{r.note ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card overflow-auto">
        <div className="card-title mb-3">Cross-Border Transfer Event Log</div>
        <table className="w-full text-sm min-w-[760px]">
          <thead className="text-ink-muted border-b border-border">
            <tr>
              <th className="text-left py-2">Event ID</th>
              <th className="text-left py-2">From</th>
              <th className="text-left py-2">To</th>
              <th className="text-left py-2">Data Type</th>
              <th className="text-left py-2">Legal Basis</th>
              <th className="text-left py-2">Status</th>
              <th className="text-left py-2">Time</th>
            </tr>
          </thead>
          <tbody>
            {mockEvents.map((e) => (
              <tr key={e.id} className="border-b border-border/50 last:border-0">
                <td className="py-2">{e.id}</td>
                <td className="py-2">{e.from}</td>
                <td className="py-2">{e.to}</td>
                <td className="py-2">{e.dataType}</td>
                <td className="py-2">{e.legalBasis}</td>
                <td className="py-2"><span className={e.status === 'Allowed' ? 'badge-good' : e.status === 'Review' ? 'badge-warn' : 'badge-bad'}>{e.status}</span></td>
                <td className="py-2">{e.happenedAt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedCountry && (
        <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setSelectedCountry(null)}>
          <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-bg-card border-l border-border p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold">{selectedCountry.countryName} ({selectedCountry.country})</h2>
                <p className="text-sm text-ink-muted mt-1">Country drill-down for data residency and compliance controls.</p>
              </div>
              <button className="rounded-md border border-border px-2 py-1 text-sm" onClick={() => setSelectedCountry(null)}>Close</button>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="card">
                <div className="card-title">Assets</div>
                <div className="kpi-value">{selectedCountry.assetCount}</div>
              </div>
              <div className="card">
                <div className="card-title">Compliance Status</div>
                <div className={selectedCountry.isCompliant ? 'kpi-value text-good' : 'kpi-value text-bad'}>
                  {selectedCountry.isCompliant ? 'Compliant' : 'Review Needed'}
                </div>
              </div>
            </div>

            <div className="mt-6">
              <div className="card-title mb-2">Asset Inventory</div>
              <div className="space-y-2">
                {countryAssets.map((asset) => (
                  <div key={asset.name} className="rounded-lg border border-border p-3 text-sm">
                    <div className="font-medium">{asset.name}</div>
                    <div className="text-ink-muted">{asset.type} · {asset.region}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <div className="card-title mb-2">Compliance Check Items</div>
              <div className="space-y-2">
                {complianceChecks.map((check) => (
                  <div key={check.item} className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
                    <span>{check.item}</span>
                    <span className={check.result === 'Pass' ? 'badge-good' : check.result === 'Review' ? 'badge-warn' : 'badge-bad'}>{check.result}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 text-sm text-ink-muted">
              Note: {selectedCountry.note ?? 'No exceptions recorded for this country.'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
